import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, message, Popconfirm, Select, Space } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { GetCursos, PostCurs, PutCurs, DeleteCurs } from '../../../dao/metadata/ManageCursos';

const { Option } = Select;

const Cursos = () => {
    const [cursos, setCursos] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<any>(null);
    const [form] = Form.useForm();
    const [addingChildTo, setAddingChildTo] = useState<any>(null);

    const loadCursos = async () => {
        setLoading(true);
        const data = await GetCursos();

        // Transform flat list to tree
        const treeStart = buildTree(data);
        setCursos(treeStart);
        setLoading(false);
    };

    const buildTree = (flatList: any[]) => {
        const roots: any[] = [];
        const map: any = {};

        // Init map
        flatList.forEach(item => {
            map[item.id] = { ...item, key: item.id, children: [] };
        });

        flatList.forEach(item => {
            if (item.parent_id) {
                if (map[item.parent_id]) {
                    map[item.parent_id].children.push(map[item.id]);
                }
            } else {
                roots.push(map[item.id]);
            }
        });

        return roots;
    };

    useEffect(() => {
        loadCursos();
    }, []);

    const handleAddRoot = () => {
        setEditingItem(null);
        setAddingChildTo(null);
        form.resetFields();
        setIsModalOpen(true);
    };

    const handleAddChild = (parent: any) => {
        setEditingItem(null);
        setAddingChildTo(parent);
        form.resetFields();
        setIsModalOpen(true);
    };

    const handleEdit = (record: any) => {
        setEditingItem(record);
        setAddingChildTo(null);
        form.setFieldsValue(record);
        setIsModalOpen(true);
    };

    const handleDelete = async (id: number) => {
        const success = await DeleteCurs(id);
        if (success) {
            message.success('Curs eliminat correctament');
            loadCursos();
        } else {
            message.error('Error al eliminar curs');
        }
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            let success = false;

            if (editingItem) {
                success = await PutCurs(editingItem.id, values.label);
            } else {
                const parentId = addingChildTo ? addingChildTo.id : null;
                // For 'value', if user sends it manually, good. If not, auto-generate or required?
                // CursosList uses things like "1DAM", "grau-superior".
                // Let's require value in form for new items.
                success = await PostCurs(parentId, values.value, values.label);
            }

            if (success) {
                message.success(editingItem ? 'Curs actualitzat' : 'Curs creat');
                setIsModalOpen(false);
                loadCursos();
            } else {
                message.error('Error en l\'operació');
            }
        } catch (error) {
            console.error('Validate Failed:', error);
        }
    };

    const columns = [
        { title: 'Etiqueta (Label)', dataIndex: 'label', key: 'label' },
        { title: 'Valor (Value)', dataIndex: 'value', key: 'value' },
        {
            title: 'Accions',
            key: 'action',
            render: (_: any, record: any) => (
                <Space>
                    <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
                    <Button icon={<PlusOutlined />} size="small" onClick={() => handleAddChild(record)} >Afegir fill</Button>
                    <Popconfirm title="Eliminar?" onConfirm={() => handleDelete(record.id)}>
                        <Button icon={<DeleteOutlined />} size="small" danger />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddRoot}>
                    Afegir Curs Arrel (Root)
                </Button>
            </div>
            <Table
                columns={columns}
                dataSource={cursos}
                loading={loading}
                pagination={false}
                defaultExpandAllRows={true}
            />

            <Modal
                title={editingItem ? "Editar Curs" : (addingChildTo ? `Afegir fill a ${addingChildTo.label}` : "Afegir Curs Arrel")}
                open={isModalOpen}
                onOk={handleOk}
                onCancel={() => setIsModalOpen(false)}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="label" label="Nom (Label)" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    {/* Value only editable on Create, to avoid breaking refs? Or allow edit? 
                        Typically 'value' should be stable. Let's allow edit if creating. */}
                    {!editingItem && (
                        <Form.Item name="value" label="Identificador (Value)" rules={[{ required: true }]}>
                            <Input placeholder="Ex: grau-superior, 1DAM, ..." />
                        </Form.Item>
                    )}
                    {editingItem && (
                        <Form.Item label="Identificador (Value)">
                            <Input value={editingItem.value} disabled />
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </div>
    );
};

export default Cursos;
